/**
 * La otra puerta: una compra sin remito. Se abre desde "Cargar a mano", al
 * lado de "Cargar remito".
 *
 * La mercaderia llego con un ticket a mano, con una foto que no se lee, o sin
 * papel. No hay archivo que subir, asi que se pide lo minimo que hace falta
 * para que el wizard pueda arrancar: el proveedor (sin el no se pasa a las
 * lineas), la moneda de las lineas y, si hay dolares en juego, el dolar que
 * tomo el proveedor: no hay uno general que lo reemplace. La fecha es hoy. Si hay un numero de
 * comprobante o un total, se corrigen despues desde la franja del encabezado.
 */
import { useState } from "react";
import type { Moneda } from "@tora/shared";

import { Campo, CampoCantidad } from "@/components/catalogo/Campos";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { Proveedor } from "@/lib/tipos";

import type { DatosCompraManual } from "./consultas";

const SIN_PROVEEDOR = "ninguno";

/** Hoy, en la zona horaria del navegador, como YYYY-MM-DD. */
export function fechaDeHoy(ahora: Date = new Date()): string {
  const y = ahora.getFullYear();
  const m = String(ahora.getMonth() + 1).padStart(2, "0");
  const d = String(ahora.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface CompraManualProps {
  proveedores: ReadonlyArray<Proveedor>;
  guardando: boolean;
  onCrear: (datos: DatosCompraManual) => void;
  onCancelar: () => void;
}

export function CompraManual({
  proveedores,
  guardando,
  onCrear,
  onCancelar,
}: CompraManualProps): JSX.Element {
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const [moneda, setMoneda] = useState<Moneda>("ARS");
  const [cotizacion, setCotizacion] = useState<number | null>(null);

  const proveedor = proveedores.find((p) => p.id === proveedorId) ?? null;
  const pideDolar = moneda === "USD" || proveedor?.moneda_lista === "USD";
  const listo = proveedor !== null && (!pideDolar || (cotizacion !== null && cotizacion > 0));

  function elegirProveedor(valor: string): void {
    if (valor === SIN_PROVEEDOR) {
      setProveedorId(null);
      return;
    }
    const elegido = proveedores.find((p) => p.id === Number(valor)) ?? null;
    setProveedorId(elegido?.id ?? null);
    // La moneda de la lista del proveedor es, casi siempre, la del remito.
    if (elegido) setMoneda(elegido.moneda_lista);
  }

  function crear(): void {
    if (proveedor === null) return;
    onCrear({
      proveedor_id: proveedor.id,
      moneda,
      fecha_comprobante: fechaDeHoy(),
      ...(pideDolar ? { cotizacion } : {}),
    });
  }

  return (
    <Card padding="normal" className="space-y-4">
      <TituloSeccion descripcion="Para cuando la mercadería llegó sin papel, o con uno que no se puede leer. Elegís el proveedor y pasás directo a cargar los productos, buscando cada uno en el catálogo.">
        Compra sin remito
      </TituloSeccion>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Campo
            label="Proveedor"
            id="manual-proveedor"
            {...(proveedores.length === 0
              ? { ayuda: "No hay proveedores cargados. Crealo primero en Proveedores." }
              : {})}
          >
            <Select
              value={proveedor ? String(proveedor.id) : SIN_PROVEEDOR}
              onValueChange={elegirProveedor}
              disabled={guardando}
            >
              <SelectTrigger id="manual-proveedor">
                <SelectValue placeholder="Elegí el proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_PROVEEDOR}>Elegí el proveedor</SelectItem>
                {proveedores.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <Campo label="Moneda de los precios" id="manual-moneda">
          <Select value={moneda} onValueChange={(v) => setMoneda(v as Moneda)} disabled={guardando}>
            <SelectTrigger id="manual-moneda">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ARS">ARS</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
        </Campo>

        {pideDolar ? (
          <CampoCantidad
            label="Cotización"
            id="manual-cotiz"
            valor={cotizacion}
            onValorChange={setCotizacion}
            placeholder="1.535,00"
            requerido
            ayuda="El dólar que te tomó el proveedor en esta compra."
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Crear no suma nada al stock. Vas a cargar y revisar los productos antes de aplicar.
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancelar} disabled={guardando}>
            Cancelar
          </Button>
          <Button type="button" onClick={crear} disabled={!listo} cargando={guardando}>
            Empezar a cargar
          </Button>
        </div>
      </div>
    </Card>
  );
}
